import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { PortalScreenComponent, ScreenComponent } from "../../models/enums/ScreenComponents";
import { DefaultRoute } from "../../models/enums/DefaultRoute";
import { DefaultPortalRoute } from "../../models/enums/DefaultPortalRoute";
import { PortalRoutes } from "../../models/enums/PortalRoutes";
import { getPrefixedStorageName } from "../../utils/storagePrefix";

// Screen hero media configuration
export interface ScreenHeroConfig {
	heroVideoUrl?: string | null;
	heroImageUrl?: string | null;
	heroOverlayOpacity?: number | null;
}

type State = {
	splashLoaded: boolean;
	showNavigationBar: boolean;
	splashVideoLoaded: boolean;
	cartDrawerOpen: boolean;
	profileDrawerOpen: boolean;
	defaultAuthRoute: DefaultRoute;
	defaultPortalAuthRoute: DefaultPortalRoute;
	currentScreen: ScreenComponent;
	portalCurrentScreen: PortalScreenComponent;
	screenList: ScreenComponent[];
	navBarItemList: ScreenComponent[];
	drawerItemList: ScreenComponent[];
	portalItemList: PortalScreenComponent[];
	currentPortalRoute: PortalRoutes;
	sideMenuIsExpanded: boolean;
	screenDisplayNames: Record<string, string>; // Map of screenKey -> displayName
	screenRoutes: Record<string, string>; // Map of screenKey -> route path (for custom URLs)
	screenHeroConfigs: Record<string, ScreenHeroConfig>; // Map of screenKey -> hero media config
};

type Action = {
	updateShowNavigationBar: (data: State['showNavigationBar']) => void;
	updateDefaultRoute: (data: State['defaultAuthRoute']) => void;
	updateDefaultPortalAuthRoute: (data: State['defaultPortalAuthRoute']) => void;
	updateCurrentScreen: (data: State['currentScreen']) => void;
	updatePortalCurrentScreen: (data: State['portalCurrentScreen']) => void;
	updatescreenList: (data: State['screenList']) => void;
	updateNavBarItemList: (data: State['navBarItemList']) => void;
	updateDrawerItemList: (data: State['drawerItemList']) => void;
	updateSplashLoaded: (data: State['splashLoaded']) => void;
	updateSplashVideoLoaded: (data: State['splashVideoLoaded']) => void;
	updateCartDrawerOpen: (data: State['cartDrawerOpen']) => void;
	updateProfileDrawerOpen: (data: State['profileDrawerOpen']) => void;
	updateCurrentPortalRoute: (data: State['currentPortalRoute']) => void;
	updateSideMenuIsExpanded: (data: State['sideMenuIsExpanded']) => void;
	updateScreenDisplayNames: (data: State['screenDisplayNames']) => void;
	updateScreenRoutes: (data: State['screenRoutes']) => void;
	updateScreenHeroConfigs: (data: State['screenHeroConfigs']) => void;
};

export const navigationStore = create(
	persist<State & Action>(
		(set) => ({
			splashLoaded: true,
			splashVideoLoaded: false,
			cartDrawerOpen: false,
			profileDrawerOpen: false,
			showNavigationBar: true,
			sideMenuIsExpanded: true,
			defaultAuthRoute: DefaultRoute.Login,
			defaultPortalAuthRoute: DefaultPortalRoute.Login,
			currentPortalRoute: PortalRoutes.Products,
			currentScreen: ScreenComponent.Shop,
			portalCurrentScreen: PortalScreenComponent.Products,
			portalItemList: [
				PortalScreenComponent.Products,
				PortalScreenComponent.Orders,
			],
		screenList: [
			ScreenComponent.Shop,
			ScreenComponent.Product,
			ScreenComponent.Orders,
			ScreenComponent.Contact,
			ScreenComponent.Cart,
			ScreenComponent.Portal,
			// Custom screens
			ScreenComponent.CapeRootsHome,
		],
			navBarItemList: [
				ScreenComponent.Shop, 
				ScreenComponent.Orders, 
			],
			drawerItemList: [
				ScreenComponent.Shop, 
				ScreenComponent.Orders, 
				ScreenComponent.Cart,
			],
			screenDisplayNames: {
				// Default display names (can be overridden by API)
				[ScreenComponent.Shop]: 'Shop',
				[ScreenComponent.Orders]: 'Orders',
				[ScreenComponent.Cart]: 'Cart',
				[ScreenComponent.Contact]: 'Contact Us',
				[ScreenComponent.CapeRootsHome]: 'Home',
			},
			screenRoutes: {
				// Default routes (can be overridden by API)
				// Custom screens can have custom routes: e.g., CapeRootsHome -> 'home' (URL: /home)
			},
			screenHeroConfigs: {
				// Hero media configs populated from API
				// e.g., CapeRootsHome -> { heroVideoUrl: '...', heroImageUrl: '...', heroOverlayOpacity: 0.4 }
			},

			updateSplashLoaded: (data) =>
				set(() => ({
					splashLoaded: data,
				})),

			updateSplashVideoLoaded: (data) =>
				set(() => ({
					splashVideoLoaded: data,
				})),

		updatescreenList: (data) =>
			set(() => ({
				screenList: data,
			})),

		updateNavBarItemList: (data) =>
			set(() => ({
				navBarItemList: data,
			})),

		updateDrawerItemList: (data) =>
			set(() => ({
				drawerItemList: data,
			})),

		updateCurrentScreen: (data) =>
				set(() => ({
					currentScreen: data,
				})),

			updatePortalCurrentScreen: (data) =>
				set(() => ({
					portalCurrentScreen: data,
				})),

			updateDefaultRoute: (data) =>
				set(() => ({
					defaultAuthRoute: data,
				})),

			updateDefaultPortalAuthRoute: (data) =>
				set(() => ({
					defaultPortalAuthRoute: data,
				})),

			updateShowNavigationBar: (data) =>
				set(() => ({
					showNavigationBar: data,
				})),
				
			updateCartDrawerOpen: (data) =>
				set(() => ({
					cartDrawerOpen: data,
				})),

			updateProfileDrawerOpen: (data) =>
				set(() => ({
					profileDrawerOpen: data,
				})),

			updateCurrentPortalRoute: (data) => {
				set(() => ({
					currentPortalRoute: data,
				}))
			},

			updateSideMenuIsExpanded: (data) =>
				set(() => ({
					sideMenuIsExpanded: data,
				})),

			updateScreenDisplayNames: (data) =>
				set((state) => ({
					screenDisplayNames: { ...state.screenDisplayNames, ...data },
				})),

			updateScreenRoutes: (data) =>
				set((state) => ({
					screenRoutes: { ...state.screenRoutes, ...data },
				})),

			updateScreenHeroConfigs: (data) =>
				set((state) => ({
					screenHeroConfigs: { ...state.screenHeroConfigs, ...data },
				})),
		}),
		{
			name: getPrefixedStorageName("navigation-storage"),
			storage: createJSONStorage(() => AsyncStorage),
			// Only persist user preferences, NOT screen lists
			// Screen lists are dynamic (from code + API) and should not be cached
			partialize: (state) => ({
				sideMenuIsExpanded: state.sideMenuIsExpanded,
				currentPortalRoute: state.currentPortalRoute,
				// Don't persist: screenList, navBarItemList, drawerItemList, currentScreen
				// These are set fresh on each load from code defaults + API custom screens config
			}) as any,
		}
	)
);
